<script setup lang="ts">
/**
 * An email draft the agent wrote, opened full height on a phone: close | Draft · subject | Send across the top, the
 * inbox composer (DraftViewer in `sheet` mode) as a card underneath, and a prompt bar at the foot that rewrites the
 * draft to an instruction. Edits autosave as they do everywhere else (the title row says so); close just closes.
 * Send runs the composer's own send, and the sheet closes the moment a send starts: the Undo toast sits above it,
 * and the mail card in the chat follows the send.
 */
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import DetentSheet from '@/components/mobile/DetentSheet.vue'
import type { ArtifactRef } from '../artifacts'
import { isDraftDiscarded, isDraftSent } from '../workspace'
import { isDraftSending } from '../draftSending'
import type { ViewerStatus } from './workspace/viewers'
import type DraftViewerComponent from './workspace/DraftViewer.vue'
import DraftPromptBar from './DraftPromptBar.vue'
import { vPress } from '@/directives/press'

const props = withDefaults(defineProps<{ open: boolean; artifact: ArtifactRef | null; layer?: 'sheet' | 'modal' }>(), { layer: 'sheet' })
const emit = defineEmits<{ close: [] }>()

/** The composer is a sheet of paper on the canvas. */
const SHEET_STYLE = { '--ds-bg': 'var(--agent-canvas, #fafafa)' }

/** The inbox composer is heavy: load it when a sheet first opens, not with every mail card. */
const DraftViewer = defineAsyncComponent(() => import('./workspace/DraftViewer.vue'))
const viewer = ref<InstanceType<typeof DraftViewerComponent> | null>(null)
const status = ref<ViewerStatus>('idle')
const subject = ref('')
const promptBar = ref<InstanceType<typeof DraftPromptBar> | null>(null)
const canSend = computed(() => Boolean(viewer.value?.canSend))
const revising = computed(() => Boolean(viewer.value?.revising))
/** The prompt bar shows while there's a composer to edit (not while loading, sending, sent or discarded). */
const canPrompt = computed(() => canSend.value || revising.value)
const statusText = computed(() => {
    if (revising.value) return 'Rewriting…'
    return { saving: 'Saving…', saved: 'Saved', error: "Couldn't save", idle: '' }[status.value]
})

watch(() => props.artifact?.id, () => {
    status.value = 'idle'
    subject.value = String(props.artifact?.data?.subject ?? props.artifact?.title ?? '')
}, { immediate: true })

// A send started (or it was sent or deleted from here): the sheet's job is done.
watch(() => {
    const id = props.artifact?.id
    return id ? isDraftSending(id) || isDraftSent(id) || isDraftDiscarded(id) : false
}, (gone, was) => {
    if (gone && !was && props.open) emit('close')
})

function send() {
    viewer.value?.send()
}

async function prompt(instruction: string) {
    if (await viewer.value?.revise(instruction)) promptBar.value?.clear()
}
</script>

<template>
    <DetentSheet
        :open="open && !!artifact"
        :detents="['large']"
        :layer="layer"
        aria-label="Email draft"
        :style="SHEET_STYLE"
        body-class="dsh-body"
        data-testid="draft-sheet"
        @close="emit('close')"
    >
        <template #header>
            <div class="dsh-head">
                <button v-press.icon type="button" class="dsh-close" aria-label="Close" data-testid="draft-sheet-cancel" @click="emit('close')">
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" /></svg>
                </button>
                <div class="dsh-title">
                    <span class="dsh-eyebrow">
                        <span class="dsh-dot" :class="{ 'is-busy': revising || status === 'saving', 'is-error': status === 'error' }" aria-hidden="true" />
                        Draft
                        <Transition name="agent-swap" mode="out-in">
                            <span v-if="statusText" :key="statusText" class="dsh-status">· {{ statusText }}</span>
                        </Transition>
                    </span>
                    <span class="dsh-title-text">{{ subject || 'New email' }}</span>
                </div>
                <button
                    v-press.pill
                    type="button"
                    class="dsh-send"
                    :disabled="!canSend"
                    data-testid="draft-sheet-send"
                    @click="send"
                >
                    Send
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 12.5V3.8M4.2 7.4L8 3.6l3.8 3.8" /></svg>
                </button>
            </div>
        </template>
        <DraftViewer
            v-if="artifact"
            ref="viewer"
            :key="artifact.id"
            :artifact="artifact"
            sheet
            @status="(s) => (status = s)"
            @title="(t) => (subject = t)"
        />
        <template #footer>
            <DraftPromptBar
                v-if="artifact"
                ref="promptBar"
                :busy="revising"
                :disabled="!canPrompt"
                :draft-key="`agent-draft:${artifact.id}`"
                :assistant-voice="Boolean(viewer?.assistantVoice)"
                @submit="prompt"
            />
        </template>
    </DetentSheet>
</template>

<style scoped>
/* The sheet header recipe (approval / question sheets): a 36px circle close, the title beside it, the action at the
   end. */
.dsh-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px 10px;
}
.dsh-close {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    color: var(--agent-text-2);
    background: var(--agent-press);
    -webkit-tap-highlight-color: transparent;
}
.dsh-close svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; }
.dsh-title { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.dsh-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--agent-text-3);
}
.dsh-dot {
    width: 6px;
    height: 6px;
    margin-right: 1px;
    border-radius: 999px;
    background: var(--agent-accent);
    transition: background-color 200ms ease;
}
.dsh-dot.is-busy { animation: dsh-pulse 1.1s ease-in-out infinite; }
.dsh-dot.is-error { background: #dc2626; }
.dsh-status { font-weight: 500; color: var(--agent-text-4); }
.dsh-title-text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 17px;
    font-weight: 650;
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: var(--agent-text-1);
}
.dsh-send {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: 5px;
    min-height: 36px;
    padding: 0 13px 0 15px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 4px 10px -4px rgb(251 76 31 / 0.5);
    -webkit-tap-highlight-color: transparent;
    transition: opacity 160ms ease, box-shadow 160ms ease;
}
.dsh-send:disabled { opacity: 0.4; box-shadow: none; }
.dsh-send svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2.1; stroke-linecap: round; stroke-linejoin: round; }
@keyframes dsh-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
    .dsh-dot.is-busy { animation: none; }
}
</style>

<style>
/* The composer fills the sheet below the header (it's paper edge to edge, iOS Mail style), so the body lays its
   content out as a column that grows. Not scoped: the body element is DetentSheet's. */
.dsh-body { display: flex; flex-direction: column; }
.dsh-body > .ds-content { display: flex; flex: 1 0 auto; flex-direction: column; }
</style>
